import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";

import { Container, ContentContainer } from "@src/core/styles";
import { useSeo } from "@src/core/hooks";
import { APP_ROUTES } from "@src/core/constants";
import {
  ActionButton,
  ArticleBody,
  Chip,
  MonoArea,
  Panel,
  PanelActions,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Section,
  SectionTitle,
  SmallButton,
  Tabs,
  ToolContainer,
  ToolIntro,
  ToolSubtitle,
  ToolTitle,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from "@src/pages/dev-tools-shared";
import { PartsGrid, QueryTable } from "./url-encoder-page.styles";

type Mode = "encode" | "decode";
type Scope = "component" | "uri";

const encodeValue = (value: string, scope: Scope): string =>
  scope === "component" ? encodeURIComponent(value) : encodeURI(value);

const decodeValue = (value: string, scope: Scope): string =>
  scope === "component" ? decodeURIComponent(value) : decodeURI(value);

const parseUrlParts = (value: string) => {
  try {
    const url = new URL(value);
    const query: { key: string; raw: string; decoded: string }[] = [];
    url.searchParams.forEach((v, k) => {
      query.push({
        key: k,
        raw: `${k}=${v}`,
        decoded: `${k}=${v}`,
      });
    });
    return {
      protocol: url.protocol,
      host: url.host,
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      query,
    };
  } catch {
    return null;
  }
};

export const UrlEncoderPage: FC = () => {
  const [mode, setMode] = useState<Mode>("encode");
  const [scope, setScope] = useState<Scope>("component");
  const [input, setInput] = useState<string>("");
  const [output, setOutput] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!input) {
      setOutput("");
      setError("");
      return;
    }
    try {
      setOutput(
        mode === "encode" ? encodeValue(input, scope) : decodeValue(input, scope)
      );
      setError("");
    } catch (e) {
      setOutput("");
      setError(
        mode === "decode"
          ? "Невалидная URL‑encoded строка."
          : "Не удалось закодировать значение."
      );
    }
  }, [input, mode, scope]);

  const parts = useMemo(() => {
    const source = mode === "decode" ? output : input;
    if (!source) return null;
    return parseUrlParts(source);
  }, [input, output, mode]);

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      enqueueSnackbar("Скопировано в буфер обмена", {
        variant: "success",
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar("Не удалось скопировать", { variant: "error" });
    }
  };

  const handleSwap = () => {
    setMode((m) => (m === "encode" ? "decode" : "encode"));
    setInput(output);
  };

  const handleClear = () => {
    setInput("");
    setOutput("");
    setError("");
  };

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "URL Encoder / Decoder онлайн",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "All",
      offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
      description:
        "Онлайн кодирование и декодирование URL: encodeURIComponent и encodeURI, парсинг query-параметров и разбор URL на части.",
      url: APP_ROUTES.URL_ENCODER,
    }),
    []
  );

  useSeo({
    title: "URL Encoder / Decoder — кодирование URL и парсинг query онлайн",
    description:
      "Бесплатный онлайн URL encode / decode (encodeURIComponent vs encodeURI), разбор URL на части и парсинг query‑параметров. Работает в браузере.",
    canonical: APP_ROUTES.URL_ENCODER,
    jsonLd,
  });

  return (
    <Container>
      <ContentContainer>
        <ToolContainer>
          <ToolIntro>
            <ToolTitle>URL Encoder / Decoder</ToolTitle>
            <ToolSubtitle>
              Кодирование и декодирование URL, разбор query‑параметров и URL на
              составные части. Поддерживаются оба режима{" "}
              <code>encodeURIComponent</code> и <code>encodeURI</code>.
            </ToolSubtitle>
          </ToolIntro>

          <Tabs>
            <Chip
              $active={mode === "encode"}
              onClick={() => setMode("encode")}
              type="button"
            >
              Кодировать
            </Chip>
            <Chip
              $active={mode === "decode"}
              onClick={() => setMode("decode")}
              type="button"
            >
              Декодировать
            </Chip>
          </Tabs>

          <Toolbar>
            <ToolbarLeft>
              <ActionButton variant="outlined" onClick={handleSwap}>
                ⇅ Поменять местами
              </ActionButton>
              <ActionButton variant="outlined" onClick={handleClear}>
                Очистить
              </ActionButton>
            </ToolbarLeft>
            <ToolbarRight>
              <Chip
                $active={scope === "component"}
                onClick={() => setScope("component")}
                type="button"
                title="Кодирует все спец-символы, включая : / ? # & ="
              >
                encodeURIComponent
              </Chip>
              <Chip
                $active={scope === "uri"}
                onClick={() => setScope("uri")}
                type="button"
                title="Не трогает резервированные символы URL (: / ? # & =)"
              >
                encodeURI
              </Chip>
            </ToolbarRight>
          </Toolbar>

          <Panel>
            <PanelHeader>
              <PanelTitle>
                {mode === "encode" ? "Исходное значение" : "Закодированная строка"}
              </PanelTitle>
            </PanelHeader>
            <PanelBody>
              <MonoArea
                multiline
                minRows={6}
                maxRows={16}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  mode === "encode"
                    ? "Например: https://example.com/search?q=привет мир"
                    : "Например: https%3A%2F%2Fexample.com%2Fsearch%3Fq%3D%D0%BF%D1%80%D0%B8%D0%B2%D0%B5%D1%82"
                }
              />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Результат</PanelTitle>
              <PanelActions>
                <SmallButton
                  variant="outlined"
                  onClick={handleCopy}
                  disabled={!output}
                >
                  Копировать
                </SmallButton>
              </PanelActions>
            </PanelHeader>
            <PanelBody>
              <MonoArea
                multiline
                minRows={6}
                maxRows={16}
                value={output}
                InputProps={{ readOnly: true }}
                placeholder="Результат появится автоматически…"
                error={!!error}
                helperText={error || undefined}
              />
            </PanelBody>
          </Panel>

          {parts && (
            <Section>
              <SectionTitle>Разбор URL</SectionTitle>
              <Panel>
                <PartsGrid as="dl">
                  <dt>protocol</dt>
                  <dd>{parts.protocol}</dd>
                  <dt>host</dt>
                  <dd>{parts.host}</dd>
                  <dt>pathname</dt>
                  <dd>{parts.pathname}</dd>
                  <dt>search</dt>
                  <dd>{parts.search || <em>(пусто)</em>}</dd>
                  <dt>hash</dt>
                  <dd>{parts.hash || <em>(пусто)</em>}</dd>
                </PartsGrid>
              </Panel>
              {parts.query.length > 0 && (
                <Panel>
                  <PanelHeader>
                    <PanelTitle>Query параметры</PanelTitle>
                  </PanelHeader>
                  <QueryTable>
                    <thead>
                      <tr>
                        <th style={{ width: "30%" }}>Ключ</th>
                        <th>Декодированное значение</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.query.map((q, idx) => (
                        <tr key={`${q.key}-${idx}`}>
                          <td>
                            <code>{q.key}</code>
                          </td>
                          <td>
                            <code>{q.decoded.split("=").slice(1).join("=")}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </QueryTable>
                </Panel>
              )}
            </Section>
          )}

          <Section>
            <SectionTitle>
              encodeURIComponent vs encodeURI — в чём разница
            </SectionTitle>
            <ArticleBody>
              <p>
                <strong>encodeURIComponent</strong> кодирует{" "}
                <em>всё, что небезопасно</em>, включая резервированные символы
                URL: <code>: / ? # [ ] @ ! $ &amp; ' ( ) * + , ; =</code>.
                Используйте, когда помещаете значение{" "}
                <strong>в параметр query</strong>, в путь или в fragment.
              </p>
              <p>
                <strong>encodeURI</strong> оставляет эти символы как есть —
                применяется, когда вы кодируете <strong>целый URL</strong> и не
                хотите сломать его структуру.
              </p>
              <p>
                <strong>Правило:</strong> если сомневаетесь —{" "}
                <code>encodeURIComponent</code>. Например, для query‑параметра{" "}
                <code>q=1+2</code> функции дадут разный результат: <code>1%2B2</code>
                против <code>1+2</code> (во втором случае сервер поймёт{" "}
                <code>+</code> как пробел).
              </p>
              <h3>Полезно знать</h3>
              <ul>
                <li>
                  Пробел в query — это <code>%20</code> или <code>+</code>{" "}
                  (последнее только в <code>application/x-www-form-urlencoded</code>).
                </li>
                <li>
                  Кириллица кодируется в UTF‑8: «привет» → <code>%D0%BF%D1%80%D0%B8%D0%B2%D0%B5%D1%82</code>.
                </li>
                <li>
                  Если нужно собрать query из объекта — используйте{" "}
                  <code>new URLSearchParams(obj).toString()</code>.
                </li>
              </ul>
              <p>
                Хотите быстро проверить, как ваш API поведёт себя на
                закодированных URL? Сгенерируйте мок‑эндпоинт в{" "}
                <a href={APP_ROUTES.DASHBOARD}>LightBoxAPI</a> за минуту.
              </p>
            </ArticleBody>
          </Section>
        </ToolContainer>
      </ContentContainer>
    </Container>
  );
};
